import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { Plus, X, Info, Paperclip, FileText, Image, File } from "lucide-react";

// Category definitions with descriptions
const TICKET_CATEGORIES = [
  {
    value: "Access & Identity",
    label: "Access & Identity",
    description: "New user setup / termination, Role or permission changes, Password resets, MFA issues, OEM or vendor portal access"
  },
  {
    value: "Hardware & Devices",
    label: "Hardware & Devices",
    description: "Laptops, desktops, tablets, Printers and scanners, Mobile phones, Peripheral issues, Device replacements"
  },
  {
    value: "Software & Applications",
    label: "Software & Applications",
    description: "DMS / CRM / F&I system issues, Software installs or updates, License requests, Application configuration questions, App performance problems"
  },
  {
    value: "Connectivity & Network",
    label: "Connectivity & Network",
    description: "Internet outages, Wi-Fi issues, VPN access, Network performance, Email connectivity"
  },
  {
    value: "Data, Reports & Integrations",
    label: "Data, Reports & Integrations",
    description: "Report discrepancies, Data corrections, Integration failures, Sync issues, Export/import requests"
  },
  {
    value: "Security & Compliance",
    label: "Security & Compliance",
    description: "Phishing, Security incidents"
  },
  {
    value: "How-To & Training",
    label: "How-To & Training",
    description: "General how-to questions, Training requests, Documentation needs"
  }
];

export default function CreateTicketModal({ user, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    priority: "medium",
    computer_name: "",
    category: "",
    dealership: user.dealership || "",
    department: user.department || "",
    additional_emails: []
  });
  const [loading, setLoading] = useState(false);
  const [dealerships, setDealerships] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const fileInputRef = useRef(null);

  // Max file size: 10MB
  const MAX_FILE_SIZE = 10 * 1024 * 1024;
  // Allowed file types
  const ALLOWED_TYPES = [
    'image/jpeg', 'image/png', 'image/gif', 'image/webp',
    'application/pdf',
    'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/plain', 'text/csv'
  ];

  useEffect(() => {
    fetchDealershipsAndDepartments();
  }, []);

  const fetchDealershipsAndDepartments = async () => {
    try {
      const [dealershipRes, departmentRes] = await Promise.all([
        axios.get(`${API}/public/dealerships`),
        axios.get(`${API}/public/departments`)
      ]);
      setDealerships(dealershipRes.data);
      setDepartments(departmentRes.data);
    } catch (error) {
      console.error("Failed to fetch dealerships/departments", error);
    }
  };

  // File attachment handlers
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = [];
    
    for (const file of files) {
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name} is too large. Maximum size is 10MB.`);
        continue;
      }
      if (!ALLOWED_TYPES.includes(file.type)) {
        toast.error(`${file.name} has an unsupported file type.`);
        continue;
      }
      validFiles.push(file);
    }
    
    if (attachments.length + validFiles.length > 5) {
      toast.error("Maximum 5 attachments allowed per ticket.");
      return;
    }
    
    setAttachments(prev => [...prev, ...validFiles]);
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const getFileIcon = (file) => {
    if (file.type.startsWith('image/')) return <Image className="w-4 h-4 text-blue-500" />;
    if (file.type === 'application/pdf') return <FileText className="w-4 h-4 text-red-500" />;
    return <File className="w-4 h-4 text-gray-500" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Computer name is left blank for user to fill manually

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all required fields
    if (!formData.title.trim()) {
      toast.error("Please enter a summary");
      return;
    }
    if (!formData.description.trim()) {
      toast.error("Please enter a description");
      return;
    }
    if (!formData.priority) {
      toast.error("Please select a priority");
      return;
    }
    if (!formData.computer_name.trim()) {
      toast.error("Please enter device name");
      return;
    }
    if (!formData.category) {
      toast.error("Please select a category");
      return;
    }
    if (!formData.dealership) {
      toast.error("Please select a dealership");
      return;
    }
    if (!formData.department) {
      toast.error("Please select a department");
      return;
    }
    
    setLoading(true);
    try {
      // Create the ticket first
      const ticketResponse = await axios.post(`${API}/tickets`, formData);
      const ticketId = ticketResponse.data.id;
      
      // Upload attachments if any
      if (attachments.length > 0) {
        const uploadPromises = attachments.map(async (file) => {
          const formDataUpload = new FormData();
          formDataUpload.append('file', file);
          try {
            await axios.post(`${API}/tickets/${ticketId}/attachments`, formDataUpload, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
          } catch (uploadError) {
            console.error(`Failed to upload ${file.name}:`, uploadError);
            toast.error(`Failed to upload ${file.name}`);
          }
        });
        await Promise.all(uploadPromises);
      }
      
      toast.success("Ticket created successfully!");
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create ticket");
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addEmailField = () => {
    setFormData(prev => ({
      ...prev,
      additional_emails: [...prev.additional_emails, ""]
    }));
  };

  const updateEmailField = (index, value) => {
    setFormData(prev => {
      const newEmails = [...prev.additional_emails];
      newEmails[index] = value;
      return { ...prev, additional_emails: newEmails };
    });
  };

  const removeEmailField = (index) => {
    setFormData(prev => ({
      ...prev,
      additional_emails: prev.additional_emails.filter((_, i) => i !== index)
    }));
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Ticket</DialogTitle>
          <DialogDescription>
            Submit a new support ticket. Our IT team will respond shortly.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              type="text"
              placeholder="Brief description of the issue"
              value={formData.title}
              onChange={(e) => updateField("title", e.target.value)}
              required
              maxLength={50}
              data-testid="ticket-title-input"
            />
            <p className="text-xs text-slate-500">{formData.title.length}/50 characters</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description *</Label>
            <Textarea
              id="description"
              placeholder="Provide detailed information about the issue..."
              value={formData.description}
              onChange={(e) => updateField("description", e.target.value)}
              required
              maxLength={500}
              rows={5}
              data-testid="ticket-description-input"
            />
            <p className="text-xs text-slate-500">{formData.description.length}/500 characters</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="priority">Priority *</Label>
              <Select value={formData.priority} onValueChange={(value) => updateField("priority", value)} required>
                <SelectTrigger data-testid="ticket-priority-select">
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="computer-name">Device Name *</Label>
              <Input
                id="computer-name"
                type="text"
                value={formData.computer_name}
                onChange={(e) => updateField("computer_name", e.target.value)}
                placeholder="Enter Device Name"
                required
                data-testid="ticket-computer-name-input"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => window.open('ms-settings:about', '_blank')}
                className="text-xs"
              >
                Find Device Name
              </Button>
            </div>

            <div className="space-y-2">
              <Label htmlFor="category">Category *</Label>
              <Select value={formData.category} onValueChange={(value) => updateField("category", value)} required>
                <SelectTrigger data-testid="ticket-category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <TooltipProvider delayDuration={200}>
                    {TICKET_CATEGORIES.map((cat) => (
                      <Tooltip key={cat.value}>
                        <TooltipTrigger asChild>
                          <SelectItem value={cat.value} className="cursor-pointer">
                            <span className="flex items-center gap-2">
                              {cat.label}
                              <Info className="w-3.5 h-3.5 text-slate-400" />
                            </span>
                          </SelectItem>
                        </TooltipTrigger>
                        <TooltipContent side="right" className="max-w-xs">
                          <p className="text-sm">{cat.description}</p>
                        </TooltipContent>
                      </Tooltip>
                    ))}
                  </TooltipProvider>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="dealership">Dealership *</Label>
              <Select value={formData.dealership} onValueChange={(value) => updateField("dealership", value)} required>
                <SelectTrigger data-testid="ticket-dealership-select">
                  <SelectValue placeholder="Select dealership" />
                </SelectTrigger>
                <SelectContent>
                  {dealerships.map((d) => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="department">Department *</Label>
              <Select value={formData.department} onValueChange={(value) => updateField("department", value)} required>
                <SelectTrigger data-testid="ticket-department-select">
                  <SelectValue placeholder="Select department" />
                </SelectTrigger>
                <SelectContent>
                  {departments.map((d) => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Additional Email Addresses */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Additional Email Addresses (Optional)</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addEmailField}
                className="flex items-center gap-1"
              >
                <Plus className="h-4 w-4" />
                Add Email
              </Button>
            </div>
            <p className="text-xs text-slate-50 dark:text-white0">
              Add email addresses that should receive notifications about this ticket.
            </p>
            {formData.additional_emails.map((email, index) => (
              <div key={`email-field-${index}`} className="flex gap-2">
                <Input
                  type="email"
                  placeholder="email@example.com"
                  value={email}
                  onChange={(e) => updateEmailField(index, e.target.value)}
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => removeEmailField(index)}
                  className="px-2"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>

          {/* Attachments Section */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-2">
                <Paperclip className="w-4 h-4" />
                Attachments (Optional)
              </Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={attachments.length >= 5}
                className="flex items-center gap-1"
                data-testid="add-attachment-button"
              >
                <Plus className="h-4 w-4" />
                Add File
              </Button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="attachment-file-input"
            />
            <p className="text-xs text-slate-500">
              Max 5 files, 10MB each. Supported: Images, PDF, Word, Excel, Text files.
            </p>
            
            {/* Attachment List */}
            {attachments.length > 0 && (
              <div className="space-y-2 mt-2">
                {attachments.map((file, index) => (
                  <div 
                    key={file.name + file.size} 
                    className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-800 rounded-lg"
                    data-testid={`attachment-item-${index}`}
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {getFileIcon(file)}
                      <span className="text-sm truncate">{file.name}</span>
                      <span className="text-xs text-slate-500">({formatFileSize(file.size)})</span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeAttachment(index)}
                      className="px-2 hover:bg-red-100 hover:text-red-600"
                      data-testid={`remove-attachment-${index}`}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} data-testid="cancel-ticket-button">
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-blue-600 hover:bg-blue-700"
              disabled={loading}
              data-testid="submit-ticket-button"
            >
              {loading ? "Creating..." : "Create Ticket"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
